import { formatNumber } from '@virtus/glide/src/utils/formatters';
import { saveAs } from 'file-saver';
import {
  CustomExportParams,
  DxDataGridInstance,
  SummaryCell,
} from 'src/DxDataGrid/utils/customExportDataGrid/exportDataGrid.model';
import { DefaultColumnWidth } from 'src/ExportToExcel/ExportToExcelConfig';

export const getDxGridExportFileName = () => {
  const fileName = 'DataGrid';
  return `${fileName}.xlsx`;
};

export const exportDataGrid = async ({
  dataGrid,
  worksheet,
  selectedRowsOnly,
  topLeftCell = { row: 1, column: 1 },
  schema,
}: {
  dataGrid?: DxDataGridInstance;
  worksheet: any;
  topLeftCell: { row: number; column: number };
  selectedRowsOnly?: boolean;
  schema: any;
}) => {
  if (!dataGrid) throw new Error("Incorrect arguments: 'dataGrid' is null");
  const allColumns = dataGrid?.state?.()?.columns.filter((column: any) => column.visible === true);
  const result = { from: { ...topLeftCell }, to: { ...topLeftCell } };
  result.to.column += allColumns?.length;

  let currentColumnIndex = result.from.column;

  const isHeadless = Boolean(dataGrid.option('headless'));
  allColumns?.sort((a: { visibleIndex: number }, b: { visibleIndex: number }) => a.visibleIndex - b.visibleIndex);
  if (allColumns) {
    worksheet.columns = allColumns?.map(({ name, dataField }: { name: string; dataField: string }) => ({
      key: dataField ?? name,
    }));
  }

  if (!isHeadless) {
    const headerRow = worksheet.getRow(result.to.row);
    for (const { caption, name, dataField } of allColumns) {
      headerRow.getCell(currentColumnIndex).value = (caption ?? name ?? dataField) as string;
      currentColumnIndex++;
    }
    result.to.row++;
  }

  const allRecords = await dataGrid.getController('data').loadAll();
  const items = selectedRowsOnly ? [allRecords.find(item => item.isSelected)] : allRecords;

  for (const item of items) {
    const dataRow = worksheet.getRow(result.to.row);
    currentColumnIndex = result.from.column;
    if (item?.rowType === 'group') {
      item?.summaryCells.forEach((summaryCell: SummaryCell[], index: number) => {
        const excelCell = dataRow.getCell(currentColumnIndex);
        if (index === item.groupIndex) {
          excelCell.value = item.values ? item.values[index] : null;
        } else if (summaryCell.length) {
          const [cell] = summaryCell;
          excelCell.value = cell.value;
        }

        // Must have a non-nil value to apply the fill style
        excelCell.value = excelCell.value == null ? '' : excelCell.value;

        currentColumnIndex++;
      });
    } else if (item?.rowType === 'data' && item.data) {
      for (const key in item?.data) {
        // Custom cell values to return while exporting based on Datatype in Glide schema
        if (schema[key]?.data_type === 'ObjectCollection') {
          dataRow.getCell(currentColumnIndex).value = item?.data[key]?.length || 0;
        } else if (schema[key]?.data_type === 'Decimal') {
          const formattedVal = schema[key].format
            ? formatNumber({
                value: Number(item?.data[key] ?? ''),
                formatString: schema[key].format.toLowerCase(),
              })
            : Number(item?.data[key] ?? '').toLocaleString(navigator.language, {
                minimumFractionDigits: 2,
              });
          dataRow.getCell(currentColumnIndex).value = formattedVal;
        } else if (schema[key]?.data_type === 'DateTime') {
          const hasValueAndFormat = item?.data[key] && schema[key].format;
          if (hasValueAndFormat) {
            let columnFormat = schema[key].format;
            if (columnFormat.display_name) {
              columnFormat = columnFormat.display_name;
            }
            const hasTime = columnFormat.split(' ').length > 1;
            const formattedValue = hasTime
              ? new Date(item?.data[key]).toLocaleString(navigator.language)
              : new Date(item?.data[key]).toLocaleDateString(navigator.language);
            dataRow.getCell(currentColumnIndex).value = formattedValue;
          } else {
            if (item?.data[key]) {
              dataRow.getCell(currentColumnIndex).value = new Date(item?.data[key]).toLocaleString(navigator.language);
            }
          }
        } else if (schema[key]?.data_type === 'Object') {
          dataRow.getCell(currentColumnIndex).value = item?.data[key]?.display_name || item?.data[key];
        } else {
          dataRow.getCell(currentColumnIndex).value = item?.data[key];
        }
        currentColumnIndex++;
      }
    }

    result.to.row++;
  }

  result.to.row--;
};

export const customExporting = async ({
  exportParams,
  selectedRowsOnly,
  sheetName = 'Main Sheet',
  schema,
}: CustomExportParams) => {
  const excelModule = await import('exceljs');
  const workbook = new excelModule.Workbook();
  const worksheet = workbook.addWorksheet(sheetName);

  await exportDataGrid({
    worksheet,
    dataGrid: exportParams.component,
    selectedRowsOnly,
    topLeftCell: { row: 1, column: 1 },
    schema,
  });

  worksheet.columns.forEach(column => (column.width = DefaultColumnWidth));

  Object.assign(worksheet.getRow(1), {
    font: { bold: true },
    alignment: { horizontal: 'center' },
  });

  // write the file
  const buffer: any = await workbook.xlsx.writeBuffer();
  const fileName = exportParams.component && getDxGridExportFileName();
  saveAs(new Blob([buffer], { type: 'application/octet-stream' }), fileName);

  // cancel the DevExtreme in-built exporting:
  exportParams.cancel = true;
};
